// terminal/src/stores/tabStore.ts

/**
 * Tab state management for multi-session terminal tabs.
 * Manages tab lifecycle, session mapping, activity indicators,
 * persistence to localStorage, and keyboard shortcuts.
 */

import type { CloseReason, SessionState } from '../types';

/**
 * Tab state within the browser
 */
export type TabStatus = 'connecting' | 'active' | 'detached' | 'terminated' | 'error';

/**
 * A single terminal tab
 */
export interface Tab {
  id: string;
  sessionId: string | null;
  name: string;
  hasActivity: boolean;
  status: TabStatus;
  /** Previous session ID for restore attempts on reconnect */
  previousSessionId: string | null;
}

/**
 * Serialized tab for localStorage persistence
 */
interface PersistedTab {
  id: string;
  sessionId: string | null;
  name: string;
}

/**
 * Events emitted by the tab store
 */
export interface TabStoreEvents {
  /** Called when a new session should be created */
  onCreateSession: (tabId: string, restoreSessionId?: string) => void;
  /** Called when a session should be closed */
  onCloseSession: (sessionId: string) => void;
  /** Called when the active tab changes (for terminal switching) */
  onActiveTabChanged: (tabId: string, sessionId: string | null) => void;
}

const STORAGE_KEY = 'whss-tabs';
// Default to 50 (enterprise) during beta — plan-based limits will be
// enforced once plan config flows through the browser session.
const DEFAULT_MAX_TABS = 50;

export class TabStore {
  private tabs: Tab[] = [];
  private activeTabId: string | null = null;
  private maxTabs: number = DEFAULT_MAX_TABS;
  private tabCounter: number = 0;
  private events: TabStoreEvents;
  private listeners: Set<() => void> = new Set();
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(events: TabStoreEvents) {
    this.events = events;
  }

  // --- State accessors ---

  getTabs(): readonly Tab[] {
    return this.tabs;
  }

  getActiveTabId(): string | null {
    return this.activeTabId;
  }

  getActiveTab(): Tab | null {
    return this.tabs.find(t => t.id === this.activeTabId) ?? null;
  }

  getTabBySessionId(sessionId: string): Tab | null {
    return this.tabs.find(t => t.sessionId === sessionId) ?? null;
  }

  getTabById(tabId: string): Tab | null {
    return this.tabs.find(t => t.id === tabId) ?? null;
  }

  getMaxTabs(): number {
    return this.maxTabs;
  }

  canCreateTab(): boolean {
    return this.tabs.length < this.maxTabs;
  }

  /**
   * Adopt an externally-created session as a tab (e.g., the legacy SessionStart
   * that the relay auto-creates on every browser connect). This creates a tab
   * entry pre-wired to the session ID WITHOUT sending CreateSession to the agent.
   * Returns the tab ID, or null if a tab for this session already exists.
   */
  adoptSession(sessionId: string, name?: string): string | null {
    // Don't adopt if we already have a tab for this session
    if (this.tabs.find(t => t.sessionId === sessionId)) {
      return null;
    }

    this.tabCounter++;
    const tabId = crypto.randomUUID();
    const tab: Tab = {
      id: tabId,
      sessionId,
      name: name ?? `Tab ${this.tabCounter}`,
      hasActivity: false,
      status: 'active',
      previousSessionId: null,
    };

    this.tabs.push(tab);
    // If no active tab, make this one active
    if (!this.activeTabId) {
      this.activeTabId = tabId;
    }
    this.notify();
    this.persistTabs();

    return tabId;
  }

  /**
   * Reassign a tab to a new session ID (used on reconnect when the relay
   * creates a new legacy session that should replace the tab's old session).
   */
  reassignSession(tabId: string, newSessionId: string): void {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return;

    tab.previousSessionId = tab.sessionId;
    tab.sessionId = newSessionId;
    tab.status = 'active';
    this.notify();
    this.persistTabs();
  }

  /**
   * Mark a tab as reconnecting (waiting for a new session from the agent).
   */
  markReconnecting(tabId: string): void {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return;

    tab.previousSessionId = tab.sessionId;
    tab.sessionId = null;
    tab.status = 'connecting';
    this.notify();
  }


  // --- Mutations ---

  setMaxTabs(max: number): void {
    this.maxTabs = max;
    this.notify();
  }

  /**
   * Create a new tab and request a session from the agent.
   * Returns the tab ID, or null if at limit.
   */
  createTab(name?: string): string | null {
    if (!this.canCreateTab()) {
      return null;
    }

    this.tabCounter++;
    const tabId = crypto.randomUUID();
    const tab: Tab = {
      id: tabId,
      sessionId: null,
      name: name ?? `Tab ${this.tabCounter}`,
      hasActivity: false,
      status: 'connecting',
      previousSessionId: null,
    };

    this.tabs.push(tab);
    this.activeTabId = tabId;
    this.notify();
    this.persistTabs();

    // Request session creation from agent
    this.events.onCreateSession(tabId);

    return tabId;
  }

  /**
   * Close a tab and its associated session.
   */
  closeTab(tabId: string): void {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return;

    // Request session close if we have one
    if (tab.sessionId) {
      this.events.onCloseSession(tab.sessionId);
    }

    this.tabs = this.tabs.filter(t => t.id !== tabId);

    // If we closed the active tab, switch to the last remaining tab
    if (this.activeTabId === tabId) {
      const newActive = this.tabs[this.tabs.length - 1] ?? null;
      this.activeTabId = newActive?.id ?? null;
      if (newActive) {
        this.events.onActiveTabChanged(newActive.id, newActive.sessionId);
      }
    }

    this.notify();
    this.persistTabs();
  }

  /**
   * Switch to a tab by ID.
   */
  switchTab(tabId: string): void {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab || this.activeTabId === tabId) return;

    // Clear activity indicator on the tab we're switching to
    tab.hasActivity = false;
    this.activeTabId = tabId;
    this.notify();

    this.events.onActiveTabChanged(tabId, tab.sessionId);
  }

  /**
   * Mark a tab as having new activity (output received while not active).
   */
  markActivity(sessionId: string): void {
    const tab = this.tabs.find(t => t.sessionId === sessionId);
    if (!tab || tab.id === this.activeTabId) return;

    tab.hasActivity = true;
    this.notify();
  }

  /**
   * Rename a tab.
   */
  renameTab(tabId: string, name: string): void {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return;

    tab.name = name.trim() || tab.name;
    this.notify();
    this.persistTabs();
  }

  // --- Agent message handlers ---

  /**
   * Handle SessionCreated response from agent.
   */
  handleSessionCreated(tabId: string, sessionId: string, _restored: boolean, _scrollbackAvailable: boolean): void {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return;

    tab.sessionId = sessionId;
    tab.status = 'active';
    this.notify();
    this.persistTabs();
  }

  /**
   * Handle SessionClosed notification from agent.
   */
  handleSessionClosed(sessionId: string, _reason: CloseReason): void {
    const tab = this.tabs.find(t => t.sessionId === sessionId);
    if (!tab) return;

    tab.status = 'terminated';
    tab.previousSessionId = tab.sessionId;
    tab.sessionId = null;
    this.notify();
    this.persistTabs();
  }

  /**
   * Handle SessionStateChanged notification from agent.
   */
  handleSessionStateChanged(sessionId: string, state: SessionState): void {
    const tab = this.tabs.find(t => t.sessionId === sessionId);
    if (!tab) return;

    if (state === 'active') {
      tab.status = 'active';
    } else if ('detached' in state) {
      tab.status = 'detached';
    } else if ('terminated' in state) {
      tab.status = 'terminated';
      tab.previousSessionId = tab.sessionId;
      tab.sessionId = null;
    }

    this.notify();
  }

  // --- Persistence ---

  /**
   * Persist tab metadata to localStorage for restore on reconnect.
   */
  persistTabs(): void {
    try {
      const data: PersistedTab[] = this.tabs.map(t => ({
        id: t.id,
        sessionId: t.sessionId,
        name: t.name,
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Restore tabs from localStorage and attempt to reconnect sessions.
   * Returns the number of tabs restored.
   */
  restoreTabs(): number {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return 0;

      const saved: PersistedTab[] = JSON.parse(stored);
      if (!Array.isArray(saved) || saved.length === 0) return 0;

      let restored = 0;
      for (const saved_tab of saved) {
        if (this.tabs.length >= this.maxTabs) break;

        const tab: Tab = {
          id: saved_tab.id,
          sessionId: null,
          name: saved_tab.name,
          hasActivity: false,
          status: 'connecting',
          previousSessionId: saved_tab.sessionId,
        };

        this.tabs.push(tab);
        restored++;

        // Update counter from restored tab names (e.g. "Tab 5" → counter = 5)
        const match = saved_tab.name.match(/^Tab (\d+)$/);
        if (match && match[1]) {
          const num = parseInt(match[1], 10);
          if (num > this.tabCounter) this.tabCounter = num;
        }

        // Try to restore the previous session, or create fresh
        if (saved_tab.sessionId) {
          this.events.onCreateSession(tab.id, saved_tab.sessionId);
        } else {
          this.events.onCreateSession(tab.id);
        }
      }

      if (this.tabs.length > 0 && !this.activeTabId) {
        this.activeTabId = this.tabs[0]?.id ?? null;
      }

      this.notify();
      return restored;
    } catch {
      return 0;
    }
  }

  /**
   * Clear persisted tabs from localStorage.
   */
  clearPersistedTabs(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
  }

  /**
   * Reset tab state without disposing listeners or keyboard shortcuts.
   * Used on reconnect to clear stale tabs from the previous connection.
   */
  reset(): void {
    this.tabs = [];
    this.activeTabId = null;
    this.tabCounter = 0;
    this.notify();
  }

  // --- Keyboard shortcuts ---

  /**
   * Register keyboard shortcuts for tab navigation.
   * - Ctrl+Tab / Ctrl+Shift+Tab: cycle tabs
   * - Ctrl+1-9: switch to tab by index
   * - Ctrl+W: close active tab (only when multiple tabs)
   * - Ctrl+T: new tab
   */
  registerKeyboardShortcuts(): void {
    if (this.keydownHandler) return;

    this.keydownHandler = (e: KeyboardEvent) => {
      // Ctrl+Tab / Ctrl+Shift+Tab: cycle tabs
      if (e.ctrlKey && e.key === 'Tab') {
        e.preventDefault();
        if (this.tabs.length <= 1) return;

        const currentIndex = this.tabs.findIndex(t => t.id === this.activeTabId);
        if (currentIndex === -1) return;

        const direction = e.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
        const nextTab = this.tabs[nextIndex];
        if (nextTab) {
          this.switchTab(nextTab.id);
        }
        return;
      }

      // Ctrl+1-9: switch to tab by index
      if (e.ctrlKey && !e.shiftKey && !e.altKey) {
        const num = parseInt(e.key, 10);
        if (num >= 1 && num <= 9) {
          e.preventDefault();
          const index = num - 1;
          const tab = this.tabs[index];
          if (tab) {
            this.switchTab(tab.id);
          }
          return;
        }
      }

      // Ctrl+W: close active tab (only if multiple tabs open)
      if (e.ctrlKey && e.key === 'w' && this.tabs.length > 1 && this.activeTabId) {
        e.preventDefault();
        this.closeTab(this.activeTabId);
        return;
      }

      // Ctrl+T: new tab
      if (e.ctrlKey && e.key === 't') {
        e.preventDefault();
        this.createTab();
        return;
      }
    };

    document.addEventListener('keydown', this.keydownHandler);
  }

  /**
   * Unregister keyboard shortcuts.
   */
  unregisterKeyboardShortcuts(): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
  }

  // --- Subscriptions ---

  /**
   * Subscribe to state changes. Returns unsubscribe function.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach(fn => fn());
  }

  // --- Cleanup ---

  dispose(): void {
    this.unregisterKeyboardShortcuts();
    this.listeners.clear();
    this.tabs = [];
    this.activeTabId = null;
  }
}

// --- Singleton ---

let storeInstance: TabStore | null = null;

export function createTabStore(events: TabStoreEvents): TabStore {
  storeInstance = new TabStore(events);
  return storeInstance;
}

export function getTabStore(): TabStore | null {
  return storeInstance;
}
